import { useRequiredContext } from "@redotech/react-util/context";
import {
  PointsSummaryBlock,
  PointsSummaryButtonStyle,
} from "@redotech/redo-model/loyalty/loyalty-page-builder/definition";
import { memo } from "react";
import ReactMarkdown from "react-markdown";
import { Flex } from "../../flex";
import { Text } from "../../text";
import {
  LoyaltyPageContext,
  LoyaltyPageEnvironment,
} from "../context/loyalty-page-context";

export const PointsSummary = memo(function PointsSummary({
  loggedInContent,
  loggedOutContent,
  allTime,
  pointsBalance,
}: PointsSummaryBlock) {
  const context = useRequiredContext(LoyaltyPageContext);
  const isLoggedIn = !!context.customer;

  return (
    <Flex dir="column" gap="3xl" px="xl" py="6xl" w="full">
      <Text
        fontSize="2xl"
        fontWeight="bold"
        style={{
          color: isLoggedIn
            ? loggedInContent.titleColor
            : loggedOutContent.titleColor,
        }}
      >
        {isLoggedIn
          ? loggedInContent.title || "Your Points Summary"
          : loggedOutContent.title || "Join Our Rewards Program"}
      </Text>

      {isLoggedIn ? (
        <LoggedInContent
          allTime={allTime}
          loggedInContent={loggedInContent}
          pointsBalance={pointsBalance}
        />
      ) : (
        <LoggedOutContent loggedOutContent={loggedOutContent} />
      )}
    </Flex>
  );
});

const LoggedInContent = memo(function LoggedInContent({
  loggedInContent,
  allTime,
  pointsBalance,
}: {
  loggedInContent: PointsSummaryBlock["loggedInContent"];
  allTime: PointsSummaryBlock["allTime"];
  pointsBalance: PointsSummaryBlock["pointsBalance"];
}) {
  const context = useRequiredContext(LoyaltyPageContext);
  if (!context.customer) return null;

  const customer = context.customer;
  const isBuilder = context.environment === LoyaltyPageEnvironment.BUILDER;

  return (
    <Flex dir="column" gap="3xl" w="full">
      {/* Points summary in two columns */}
      <Flex dir="row" gap="3xl">
        {/* Left column - Current points (3/5 width) */}
        <Flex dir="column" style={{ flex: 3 }}>
          <Text
            fontSize="2xl"
            fontWeight="bold"
            style={{ color: pointsBalance.color || "#000000" }}
          >
            {customer.currentPoints.toLocaleString()}
          </Text>
          <Text fontSize="sm" textColor="tertiary">
            Current Points
          </Text>
          <Text fontSize="xs" textColor="tertiary">
            Worth ${customer.currentMoney.toFixed(2)}
          </Text>
        </Flex>

        {/* Right column - All time stats (2/5 width) */}
        <Flex dir="column" gap="lg" style={{ flex: 2 }}>
          {allTime.pointsEarned.enabled && (
            <Flex dir="column" gap="none">
              <Text
                fontSize="lg"
                fontWeight="bold"
                style={{ color: allTime.pointsEarned.color }}
              >
                {customer.pointsEarned.toLocaleString()}
              </Text>
              <Text fontSize="xs" textColor="tertiary">
                All-Time Points Earned
              </Text>
            </Flex>
          )}

          {allTime.moneySpent.enabled && (
            <Flex dir="column" gap="none">
              <Text
                fontSize="lg"
                fontWeight="bold"
                style={{ color: allTime.moneySpent.color }}
              >
                ${customer.moneySpent.toLocaleString()}
              </Text>
              <Text fontSize="xs" textColor="tertiary">
                All-Time Money Spent
              </Text>
            </Flex>
          )}

          {allTime.pointsSpent.enabled && (
            <Flex dir="column" gap="none">
              <Text
                fontSize="lg"
                fontWeight="bold"
                style={{ color: allTime.pointsSpent.color }}
              >
                {customer.pointsSpent.toLocaleString()}
              </Text>
              <Text fontSize="xs" textColor="tertiary">
                All-Time Points Spent
              </Text>
            </Flex>
          )}
        </Flex>
      </Flex>

      {/* Body content */}
      {(loggedInContent.body || isBuilder) && (
        <Text fontSize="md" style={{ color: loggedInContent.bodyColor }}>
          <ReactMarkdown>
            {loggedInContent.body ||
              (isBuilder
                ? "Customize this section with information about how your customers can use their points."
                : "")}
          </ReactMarkdown>
        </Text>
      )}
    </Flex>
  );
});

const LoggedOutContent = memo(function LoggedOutContent({
  loggedOutContent,
}: {
  loggedOutContent: PointsSummaryBlock["loggedOutContent"];
}) {
  return (
    <Flex dir="column" gap="lg">
      <Text fontSize="md" style={{ color: loggedOutContent.bodyColor }}>
        <ReactMarkdown>
          {loggedOutContent.body ||
            "Sign up to start earning points with every purchase."}
        </ReactMarkdown>
      </Text>

      {/* TODO: aradford: Where should this redirect the user to? */}
      <Flex>
        <Flex
          as="button"
          style={{
            backgroundColor:
              loggedOutContent.buttonStyle === PointsSummaryButtonStyle.FILLED
                ? "#000000"
                : "transparent",
            color:
              loggedOutContent.buttonStyle === PointsSummaryButtonStyle.FILLED
                ? "#ffffff"
                : "#000000",
            border:
              loggedOutContent.buttonStyle === PointsSummaryButtonStyle.GHOST
                ? "none"
                : "1px solid #000000",
            padding: "8px 16px",
            borderRadius: "4px",
            cursor: "pointer",
          }}
        >
          Sign Up for Rewards
        </Flex>
      </Flex>
    </Flex>
  );
});
